/**
 * Given a schema, produce a reasonable default value. Used when the user clicks
 * "Add page" — we need to synthesize a valid-ish new item rather than inserting
 * null and making the UI crash.
 *
 * "Reasonable" means: use `default` if present, use `const` if present,
 * otherwise produce the empty value for that type. We do NOT fabricate realistic
 * content — that's the AI feature's job.
 */

import type { JsonSchema, JsonValue } from '@bliksys/shared';

import { resolveSchema } from './resolver.js';

export function defaultValueFor(schema: JsonSchema, rootSchema: JsonSchema): JsonValue {
  const resolved = resolveSchema(schema, rootSchema);

  if (resolved.default !== undefined) return resolved.default;
  if (resolved.const !== undefined) return resolved.const;

  // For oneOf with a discriminator, pick the first variant and build its
  // default — UNLESS the union is `discriminatorLocked`, in which case
  // we deliberately return null so the renderer shows a "pick a type"
  // empty state. Auto-defaulting locked unions silently picks the first
  // variant and then immutability prevents the user from changing it.
  const variants = resolved.oneOf ?? resolved.anyOf;
  if (variants && variants.length > 0) {
    if (resolved['x-bliksys']?.discriminatorLocked === true) return null;
    const first = variants[0];
    if (first) return defaultValueFor(first, rootSchema);
  }

  if (resolved.enum && resolved.enum.length > 0) {
    return resolved.enum[0] ?? null;
  }

  const rawType = Array.isArray(resolved.type)
    ? resolved.type.find((t) => t !== 'null')
    : resolved.type;

  // Infer type from shape when missing — JSON Schema in the wild frequently
  // omits `type` on objects-with-properties (see the fstab example).
  const type = rawType ?? (resolved.properties ? 'object' : resolved.items ? 'array' : undefined);

  switch (type) {
    case 'object': {
      const obj: Record<string, JsonValue> = {};
      const required = new Set(resolved.required ?? []);
      for (const [key, propSchema] of Object.entries(resolved.properties ?? {})) {
        if (required.has(key)) {
          obj[key] = defaultValueFor(propSchema, rootSchema);
        } else if (propSchema.default !== undefined) {
          obj[key] = propSchema.default;
        }
      }
      return obj;
    }
    case 'array':
      return [];
    case 'string':
      return '';
    case 'number':
    case 'integer': {
      // Respect min/max so defaults are immediately valid. Clamp 0 into
      // [minimum, maximum] when bounds are set; otherwise 0.
      const min = resolved.minimum ?? resolved.exclusiveMinimum;
      const max = resolved.maximum ?? resolved.exclusiveMaximum;
      if (min !== undefined && 0 < min) return min;
      if (max !== undefined && 0 > max) return max;
      return 0;
    }
    case 'boolean':
      return false;
    case 'null':
      return null;
    default:
      return null;
  }
}
